import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, Copy, Bot } from 'lucide-react';
import { toast } from 'sonner';

const ChatMessage = ({ message }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success('Copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  // Parse markdown-like content
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let inList = false;

    lines.forEach((line, index) => {
      // Headers
      if (line.startsWith('### ')) {
        if (inList && currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(
          <h3 key={index} className="text-base font-semibold mt-4 mb-2 text-[#00B7D4]">
            {line.replace('### ', '')}
          </h3>
        );
      } 
      // Bold headers (** **)
      else if (line.startsWith('**') && line.endsWith('**')) {
        if (inList && currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        const text = line.replace(/\*\*/g, '');
        
        // Check for action horizons
        if (text.includes('Hoy') || text.includes('48h')) {
          elements.push(
            <div key={index} className="flex items-center gap-2 mt-4 mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-medium">HOY</span>
              <span className="font-semibold">{text}</span>
            </div>
          );
        } else if (text.includes('2-4 semanas')) {
          elements.push(
            <div key={index} className="flex items-center gap-2 mt-4 mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500 text-white font-medium">2-4 SEM</span>
              <span className="font-semibold">{text}</span>
            </div>
          );
        } else if (text.includes('1-3 meses')) {
          elements.push(
            <div key={index} className="flex items-center gap-2 mt-4 mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#00B7D4] text-white font-medium">1-3 MES</span>
              <span className="font-semibold">{text}</span>
            </div>
          );
        } else {
          elements.push(
            <p key={index} className="font-semibold mt-3 mb-1">{text}</p>
          );
        }
      }
      // List items
      else if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        inList = true;
        const text = line.trim().replace(/^[-•]\s*/, '');
        // Format inline bold
        const formattedText = text.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
          }
          return part;
        });
        currentList.push(<li key={`item-${index}`} className="text-sm">{formattedText}</li>);
      }
      // Numbered items
      else if (/^\d+\.\s/.test(line.trim())) {
        if (inList && currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
          currentList = [];
        }
        inList = true;
        const text = line.trim().replace(/^\d+\.\s*/, '');
        const formattedText = text.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
          }
          return part;
        });
        currentList.push(<li key={`item-${index}`} className="text-sm">{formattedText}</li>);
      }
      // Code blocks
      else if (line.includes('`')) {
        if (inList && currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        const parts = line.split(/(`[^`]+`)/).map((part, i) => {
          if (part.startsWith('`') && part.endsWith('`')) {
            return (
              <code key={i} className="px-1.5 py-0.5 bg-secondary rounded text-xs font-mono">
                {part.replace(/`/g, '')}
              </code>
            );
          }
          return part;
        });
        elements.push(<p key={index} className="mb-2 text-sm">{parts}</p>);
      }
      // Regular paragraphs
      else if (line.trim()) {
        if (inList && currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(<p key={index} className="mb-2 text-sm">{line}</p>);
      }
    });

    // Flush remaining list
    if (currentList.length > 0) {
      elements.push(<ul key="final-list" className="list-disc pl-5 mb-3 space-y-1">{currentList}</ul>);
    }

    return elements;
  };

  if (isUser) {
    return (
      <div className="flex justify-end" data-testid="user-message">
        <div className="max-w-[80%] bg-[#00B7D4] text-white p-4 rounded-2xl rounded-tr-sm">
          <p className="text-sm">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3" data-testid="assistant-message">
      <div className="h-8 w-8 rounded-full bg-[#00B7D4]/10 flex items-center justify-center flex-shrink-0">
        <Bot className="h-4 w-4 text-[#00B7D4]" />
      </div>
      <div className="flex-1 max-w-[90%]">
        <div className="bg-white border border-border/50 p-4 rounded-2xl rounded-tl-sm shadow-sm">
          {message.latencyMs && (() => {
            const totalSeconds = Math.round(message.latencyMs / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            return (
              <div className="text-xs text-muted-foreground mb-3 text-right">
                Respuesta generada en {timeString}
              </div>
            );
          })()}
          <div className="message-content">
            {renderContent(message.content)}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-[#00B7D4]"
            onClick={copyToClipboard}
            data-testid="copy-message-btn"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Copiado
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 mr-1" />
                Copiar plan de acción
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
